import {
  Component,
  EventEmitter,
  Output,
  ViewChild,
  OnDestroy,
  AfterViewInit,
  HostListener,
} from '@angular/core';
import { Store } from '@ngrx/store';
import {
  StudioService,
  StudioAnimationService,
  saveScene,
  selectCurrentStudioScene,
} from '../../element-editor/store/studio';
import {
  StudioAnimation,
  StudioScene,
  StudioSceneBackgroundDefinition,
  ImageAnimation,
} from '../../element-editor/store/studio/studio.types';
import {
  LayoutCell,
  RootLayoutCell,
} from '../../element-editor/store/studio/layout/layout-cell';
import {
  StudioAnimationMetaData,
  LayoutAnimationFamily,
} from '../../element-editor/store/studio/animation/animation.types';
import { CanvasService } from '../../services/canvas/canvas.service';
import { cloneDeep, set } from 'lodash';
import { ScenePreviewComponent } from './scene-preview/scene-preview.component';

@Component({
  selector: 'nw-animation-settings-modal',
  templateUrl: './animation-settings-modal.component.html',
  styleUrls: ['./animation-settings-modal.component.scss'],
})
export class AnimationSettingsModalComponent
  implements AfterViewInit, OnDestroy
{
  @Output() close = new EventEmitter<void>();
  @ViewChild(ScenePreviewComponent) preview: ScenePreviewComponent;

  // Cell selector
  rootCell: RootLayoutCell | null = null;
  isBackgroundSelected = false;

  // Left panel list
  listItems: { label: string; key: string; options?: any[] }[] = [];
  selectedListItem: { label: string; key: string; options?: any[] } | null =
    null;
  listMode: 'layout' | 'text' | null = null;

  // Sub-items for text animations (options within a category)
  textSubItems: { key: string; label?: string; options?: string[] }[] = [];
  selectedSubItem: string | null = null;
  selectedOptionIndex = -1;

  // Working state
  workingAnimation: StudioAnimation | null = null;
  workingItemAnimation: StudioAnimationMetaData | null = null;

  // Background move form
  bgAnimation: ImageAnimation = {};
  showBgEditor = false;

  get currentScene(): StudioScene | undefined {
    return this.studioService.currentSceneShape?.scene;
  }

  localScene: StudioScene | null = null;

  constructor(
    private store: Store,
    private studioService: StudioService,
    private animationService: StudioAnimationService,
    private cs: CanvasService,
  ) {
    this.store.select(selectCurrentStudioScene).subscribe(scene => {
      this.localScene = cloneDeep(scene);
    });
  }

  ngAfterViewInit() {
    this.init();
  }

  private init() {
    if (!this.localScene) return;

    this.workingAnimation = this.localScene.animation
      ? cloneDeep(this.localScene.animation)
      : null;

    if (this.localScene.layoutDefinition) {
      this.rootCell = new RootLayoutCell(
        this.localScene,
        this.localScene.layoutDefinition,
        {
          W: this.cs.currentScreenX,
          H: this.cs.currentScreenY,
        },
      );
      this.selectNode(this.rootCell);
    } else {
      this.rootCell = null;
    }
  }

  // --- Cell selection ---

  get selectedNode(): LayoutCell | null {
    return this.rootCell?.selectedCell ?? null;
  }

  get activeAppearVariant(): string | null {
    return (
      this.selectedNode?.definition.animationMetaData?.appear?.variant ?? null
    );
  }

  isItemActive(item: { key: string }): boolean {
    const options = this.selectedNode?.getAnimationOptions('appear');
    if (!options) return false;
    return options.items.find(i => i.key === item.key)?.isActive ?? false;
  }

  selectNode(node: LayoutCell) {
    if (!this.rootCell) return;
    this.isBackgroundSelected = false;
    this.showBgEditor = false;
    this.rootCell.selectCell(node.id);
    this.selectedListItem = null;
    this.selectedSubItem = null;
    this.textSubItems = [];
    this.workingItemAnimation = null;
    this.preview?.reset();
    this.updateListForSelectedNode();
    this.restoreItemAnimationSelection();
  }

  selectBackground() {
    this.isBackgroundSelected = true;
    if (this.rootCell) {
      this.rootCell.selectedCell = null;
    }
    this.selectedListItem = null;
    this.selectedSubItem = null;
    this.textSubItems = [];
    this.workingItemAnimation = null;
    this.listMode = null;
    this.listItems = [{ label: 'Move', key: 'move' }];
    this.bgAnimation = cloneDeep(this.localScene?.background?.animation ?? {});
    this.preview?.reset();
  }

  onBgCropChange(field: 'x' | 'y' | 'scale', value: number) {
    if (!this.bgAnimation.crop) {
      this.bgAnimation.crop = { x: 0, y: 0, scale: 1 };
    }
    this.bgAnimation.crop[field] = value;
    this.syncBgAnimation();
  }

  onBgCellChange(field: 'x' | 'y' | 'w' | 'h', value: number) {
    if (!this.bgAnimation.cell) {
      this.bgAnimation.cell = { x: 0, y: 0, w: 1, h: 1 };
    }
    this.bgAnimation.cell[field] = value;
    this.syncBgAnimation();
  }

  toggleBgEditor() {
    console.log('toggleBgEditor', this.localScene);
    this.showBgEditor = !this.showBgEditor;
  }

  onBgDefChange(def: StudioSceneBackgroundDefinition) {
    if (!this.localScene) return;
    // Preserve existing animation when changing background type
    if (this.localScene.background?.animation) {
      def.animation = this.localScene.background.animation;
    }
    set(this.localScene, ['background'], def);
    this.deriveSceneAnimation();
  }

  onBgEditorCropChange(crop: ImageAnimation['crop']) {
    this.bgAnimation.crop = crop;
    this.syncBgAnimation();
  }

  private syncBgAnimation() {
    set(
      this.localScene,
      ['background', 'animation'],
      cloneDeep(this.bgAnimation),
    );
    this.deriveSceneAnimation();
  }

  private restoreItemAnimationSelection() {
    const node = this.selectedNode;
    if (!node) return;

    const saved = node.definition.animationMetaData?.appear;
    if (!saved || this.listMode !== 'text') return;

    const familyItem = this.listItems.find(i => i.key === saved.family);
    if (!familyItem) return;
    this.selectedListItem = familyItem;
    this.textSubItems = familyItem.options || [];

    const subItem = this.textSubItems.find((s: any) => s.key === saved.variant);
    if (subItem) {
      this.selectedSubItem = subItem.key;
    }

    this.workingItemAnimation = saved;
  }

  // --- Keyboard navigation ---

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (!this.listItems.length) return;

    switch (event.key) {
      case 'ArrowUp':
      case 'ArrowDown': {
        event.preventDefault();
        const dir = event.key === 'ArrowDown' ? 1 : -1;
        const currentIdx = this.selectedListItem
          ? this.listItems.indexOf(this.selectedListItem)
          : -1;
        const nextIdx = Math.max(
          0,
          Math.min(this.listItems.length - 1, currentIdx + dir),
        );
        this.selectListItem(this.listItems[nextIdx]);
        break;
      }

      case 'ArrowRight':
      case 'ArrowLeft': {
        if (!this.textSubItems.length) break;
        event.preventDefault();
        const dir = event.key === 'ArrowRight' ? 1 : -1;
        const currentIdx = this.selectedSubItem
          ? this.textSubItems.findIndex(s => s.key === this.selectedSubItem)
          : -1;
        const nextIdx = Math.max(
          0,
          Math.min(this.textSubItems.length - 1, currentIdx + dir),
        );
        this.selectSubItem(this.textSubItems[nextIdx]);
        break;
      }

      case 'Tab': {
        const sub = this.textSubItems.find(s => s.key === this.selectedSubItem);
        if (!sub?.options?.length || !this.selectedListItem) break;
        event.preventDefault();
        const dir = event.shiftKey ? -1 : 1;
        this.selectedOptionIndex =
          (this.selectedOptionIndex + dir + sub.options.length) %
          sub.options.length;
        this.applyTextAnimation(
          this.selectedListItem.key,
          sub.key,
          sub.options[this.selectedOptionIndex],
        );
        break;
      }
    }
  }

  // --- Left panel list population ---

  private updateListForSelectedNode() {
    const node = this.selectedNode;
    if (!node) {
      this.listMode = null;
      this.listItems = [];
      return;
    }

    const options = node.getAnimationOptions('appear');
    this.listMode = options.mode;
    this.listItems = options.items;
  }

  selectListItem(item: { label: string; key: string; options?: any[] }) {
    this.selectedListItem = item;
    this.selectedSubItem = null;
    this.selectedOptionIndex = 0;

    if (this.listMode === 'layout') {
      this.textSubItems = [];
      this.applyLayoutAnimation(item.key as LayoutAnimationFamily);
    } else if (this.listMode === 'text') {
      this.textSubItems = item.options || [];
      const firstSub = this.textSubItems[0];
      if (firstSub) {
        this.selectedSubItem = firstSub.key;
        const firstOption = firstSub.options?.[0];
        this.applyTextAnimation(item.key, firstSub.key, firstOption);
      }
    } else {
      this.textSubItems = [];
    }
  }

  private applyLayoutAnimation(family: LayoutAnimationFamily) {
    const scene = this.currentScene;
    if (!scene || !this.rootCell) return;

    const steps = this.rootCell.getLayoutAnimationSteps(family);
    const animation = this.animationService.convertLayoutAnimation(
      scene,
      steps,
    );

    this.selectedNode?.setSelectedAnimationMetaData('appear', family);
    this.workingAnimation = animation;

    set(this.localScene, ['layoutDefinition'], this.rootCell.definition);
    set(this.localScene, ['animation'], animation);

    this.preview?.patchSceneAnimation(animation);
  }

  selectSubItem(subItem: { key: string; label?: string; options?: string[] }) {
    this.selectedSubItem = subItem.key;
    this.selectedOptionIndex = 0;
    if (this.selectedListItem) {
      const firstOption = subItem.options?.[0];
      this.applyTextAnimation(
        this.selectedListItem.key,
        subItem.key,
        firstOption,
      );
    }
  }

  private applyTextAnimation(
    familyKey: string,
    subKey: string,
    option?: string,
  ) {
    const node = this.selectedNode;
    if (!node || !this.preview?.sceneShape) return;

    const shape = this.preview.sceneShape.shapes[node.definition.shape];
    if (!shape) return;

    const animation = node.buildAppearAnimation(familyKey, subKey, option);
    if (!animation) return;

    set(
      this.localScene,
      ['shapes', shape.id, 'animationFunctions', 'appear'],
      animation,
    );

    this.preview.patchShapeAnimation(node.definition.shape, animation);
  }

  private deriveSceneAnimation() {
    if (!this.rootCell || !this.localScene) return;
    const chain = this.rootCell.getStudioAnimation();
    set(this.localScene, ['animation'], chain || undefined);
    this.preview?.patchSceneAnimation(chain);
  }

  // --- Preview controls ---

  playPreview() {
    this.preview?.play();
  }

  resetPreview() {
    this.preview?.reset();
  }

  // --- Apply & Close ---

  applyAndClose() {
    this.deriveSceneAnimation();
    this.store.dispatch(saveScene({ value: this.localScene }));
    this.onClose();
  }

  onClose() {
    this.close.emit();
  }

  regenerateOptions() {
    // No-op for now
  }

  ngOnDestroy() {
    // Preview component handles its own cleanup
  }
}
